import React, { useEffect, useState } from "react";
import { IconMail, IconPhone } from "../components/ui/icons";
import Card from "../components/ui/Card";
import { EmptyState } from "../components/ui/StateBlock";
import { CONTACT, SUPPORT } from "../config/contact";

const linkClass =
  "text-primary underline-offset-2 hover:underline dark:text-primary-light";

// "www.firma.rs" -> "https://www.firma.rs" (the user does not have to type the protocol)
export function webHref(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

// Only the recipient and Subject; the message body is intentionally not sent, the user writes it.
export function supportMailto(email, subject) {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}`;
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0 sm:flex-row sm:gap-4">
      <dt className="w-24 shrink-0 text-xs text-slate-500 dark:text-slate-400 sm:pt-0.5">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-slate-800 dark:text-slate-100">{children}</dd>
    </div>
  );
}

// The details are read at runtime from contact.json next to index.html, so they can be edited on the server
// without a rebuild and are never overwritten by a new build. Falls back to src/config/contact.js.
function useContactConfig(contactProp, supportProp) {
  const [remote, setRemote] = useState(null);
  const skip = Boolean(contactProp || supportProp);
  useEffect(() => {
    if (skip || typeof fetch !== "function") return undefined;
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL || ""}/contact.json`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json && typeof json === "object") setRemote(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [skip]);
  return {
    contact: contactProp || { ...CONTACT, ...(remote?.contact || {}) },
    support: supportProp || { ...SUPPORT, ...(remote?.support || {}) },
  };
}

export default function Contact({ contact: contactProp, support: supportProp }) {
  const { contact, support } = useContactConfig(contactProp, supportProp);
  const hasContact = Boolean(contact.company || contact.address || contact.city || contact.web || contact.email);
  const hasSupport = Boolean(support.phone || support.email);

  return (
    <div className="flex max-w-xl flex-col gap-4">
      <Card title="Kontakt">
        {hasContact ? (
          <>
            {contact.company && (
              <p className="mb-3 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">{contact.company}</p>
            )}
            <dl className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
              {contact.address && <Row label="Adresa">{contact.address}</Row>}
              {contact.city && <Row label="Grad">{contact.city}</Row>}
              {contact.web && (
                <Row label="Web">
                  <a href={webHref(contact.web)} target="_blank" rel="noopener noreferrer" className={linkClass}>
                    {contact.web}
                  </a>
                </Row>
              )}
              {contact.email && (
                <Row label="Email">
                  <a href={`mailto:${contact.email}`} className={linkClass}>
                    {contact.email}
                  </a>
                </Row>
              )}
            </dl>
          </>
        ) : (
          <EmptyState title="Kontakt podaci nisu uneti" hint="Upiši ih u fajl contact.json pored index.html na serveru." />
        )}
      </Card>

      <Card title="Tehnička podrška">
        {hasSupport ? (
          <>
            <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
              Ako nešto ne radi kako treba ili imaš pitanje oko korišćenja aplikacije, javi se direktno.
            </p>
            <dl className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
              {support.phone && (
                <Row label="Telefon">
                  <a href={`tel:${support.phone.replace(/[^\d+]/g, "")}`} className={`${linkClass} inline-flex items-center gap-2 tabular-nums`}>
                    <IconPhone aria-hidden="true" className="shrink-0" />
                    {support.phone}
                  </a>
                </Row>
              )}
              {support.email && (
                <Row label="E-mail">
                  <a href={supportMailto(support.email, support.subject)} className={`${linkClass} inline-flex items-center gap-2`}>
                    <IconMail aria-hidden="true" className="shrink-0" />
                    {support.email}
                  </a>
                </Row>
              )}
            </dl>
          </>
        ) : (
          <EmptyState title="Podaci o podršci nisu uneti" hint="Upiši phone i email u fajl contact.json pored index.html na serveru." />
        )}
      </Card>
    </div>
  );
}
